import Joi from 'joi';
import _ from 'lodash';

import PropTypes from 'prop-types';
import Form from '../../../common/form';

import { getDateFromString } from '../../../../utils/utilities';
import { getAdditionalEquipment } from '../../../../utils/data';
import type { EquipmentOption } from '../../../../utils/models';

import toastService from '../../../../services/toastService';
class AUTGIAutEquipmentForm extends Form {
	state = {
		data: {
			autSolution: '',
			autSolutionSN: '',
			autEquipmentBrand: '',
			inspectionProbes: '',
			inspectionProbesSN: '',
			dateOfInspection: new Date(),
			calibrationDueDate: new Date(),
		},
		errors: {},
		equipment: [],
		loading: false,
		progress: 0,
	};

	schema = {
		autSolution: Joi.object({
				label: Joi.string().required(),
				value: Joi.string().required(),
				serialNumber: Joi.string().optional(),
				__isNew__: Joi.boolean().optional(),
			})
			.required()
			.label('Aut Solution'),
		autSolutionSN: Joi.string().required().label('Aut Solution SN'),
		autEquipmentBrand: Joi.string().required().label('AUT Equipment Brand'),
		inspectionProbes: Joi.string().min(3).required().label('Inpection Probes'),
		inspectionProbesSN: Joi.string().min(3).required().label('Inpection Probes SN'),
		dateOfInspection: Joi.date().less('now').required().label('Date of Inspection'),
		calibrationDueDate: Joi.date().greater('now').required().label('Calibration Due Date'),
	};

	static propTypes = {
		initialData: PropTypes.object,
		onProceed: PropTypes.func,
	};

	componentDidMount() {
		if (this.props.initialData) {
			const initialData = { ...this.props.initialData };

			if (_.isString(initialData.calibrationDueDate)) {
				initialData.calibrationDueDate = getDateFromString(initialData.calibrationDueDate);
			}
			
			if (_.isString(initialData.dateOfInspection)) {
				initialData.dateOfInspection = getDateFromString(initialData.dateOfInspection);
			}

			if (_.isString(initialData.autSolution)) {
				initialData.autSolution = this.createNewMultiSelectOption(initialData.autSolution);
				initialData.autSolutionSN = this.getEquipmentSerialNumber(initialData.autSolution);
			}

			this.setState({ data: initialData, equipment: getAdditionalEquipment() } as any);
		}
	}

	componentDidUpdate(prevProps: Readonly<any>, prevState: Readonly<{ data: any; errors: any; progress: number; loading: boolean; touched?: boolean; }>, snapshot?: any): void {
		if (this.state.data.autSolution) {
			const serialNumber = this.getEquipmentSerialNumber(this.state.data.autSolution);

			if (serialNumber !== this.state.data.autSolutionSN) {
				this.setState({ data: { ...this.state.data, autSolutionSN: serialNumber }});
			}
		}
	}

	doSubmit = (): void => {
		this.props.onProceed(this.getApiData());
		toastService.success('AUT Equipment Information saved successfully!');
	};

	getApiData = () => {
		return {
			autSolution: (this.state.data.autSolution as unknown as EquipmentOption).value,
			autSolutionSN: this.state.data.autSolutionSN,
			autEquipmentBrand: this.state.data.autEquipmentBrand,
			inspectionProbes: this.state.data.inspectionProbes,
			inspectionProbesSN: this.state.data.inspectionProbesSN,
			dateOfInspection: this.state.data.dateOfInspection,
			calibrationDueDate: this.state.data.calibrationDueDate,
		}
	}

	getEquipmentSerialNumber = (autSolution: EquipmentOption | string) => {
		const equipmentOption = autSolution as unknown as EquipmentOption;

		if (equipmentOption) {
			const equipmentMatch = (this.state.equipment as EquipmentOption[]).find((equipment) => equipment.label === equipmentOption.value);
			if (equipmentMatch) return equipmentMatch.serialNumber;
		}

		return '';
	};

	handleCreateMultiSelectOption = (newEquipmentName: string) => {
		const newItemName = this.createNewMultiSelectOption(newEquipmentName);
		const data = { ...this.state.data, autSolution: newItemName };

		this.setState({ data });
	};

	render() {
		return (
			<form autoComplete="off" onSubmit={this.handleSubmit}>
				<div className="form-title">AUT Equipment Information</div>

				<div className="divider my-4"></div>

				<div className="container-fluid">
					<div className="row">
						<div className="col">
							{this.renderCreatableMultiselect({
								closeMenuOnSelect: true,
								options: this.state.equipment,
								name: 'autSolution',
								label: 'Aut Solution',
								placeholder: 'Enter Aut Solution',
								isMulti: false,
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'autSolutionSN',
								label: 'Aut Solution SN',
								placeholder: 'Enter Aut Solution SN',
								disabled: !!this.getEquipmentSerialNumber(this.state.data.autSolution),
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'autEquipmentBrand',
								label: 'Aut Equipment Brand',
								placeholder: 'Enter Aut Equipment Brand',
							})}
						</div>

						<div className="col">
							{this.renderDatePicker({
								name: 'dateOfInspection',
								label: 'Date of Inspection',
								placeholderText: 'Select Date of Inspection',
								maxDate: new Date(),
							})}
						</div>
					</div>
				</div>

				<div className="divider my-4"></div>

				<div className="container-fluid overflow-hidden">
					<div className="row">
						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'inspectionProbes',
								label: 'Inpection Probes',
								placeholder: 'Enter Inpection Probes',
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'inspectionProbesSN',
								label: 'Inpection Probes SN',
								placeholder: 'Enter Inpection Probes SN',
							})}
						</div>
						
						<div className="col">
							{this.renderDatePicker({
								name: 'calibrationDueDate',
								label: 'Calibration Due Date',
								placeholderText: 'Select Calibration Due Date',
								minDate: new Date(),
							})}
						</div>
					</div>
				</div>

				<div className="divider mt-4 mb-2"></div>

				<div className="d-flex justify-content-end">
					{this.renderButton('Save Equipment Information')}
				</div>
			</form>
		);
	}
}

export default AUTGIAutEquipmentForm;